import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Edit3, 
  Eye, 
  Sparkles
} from 'lucide-react';
import GenerationDisplay from '@/components/survey/GenerationDisplay';
import { useTranslation } from 'react-i18next';
interface SurveyQuestionsPanelProps {
  generatedQuestions: any[];
  answers: Record<string, any>;
  mode: string;
  currentGeneration?: any;
  user?: any;
  toolId: string;
  currentLang: string;
  onQuestionChange?: (index: number, field: string, value: any) => void;
  onAnswerChange?: (questionId: string, value: any) => void;
  onAddQuestion?: () => void;
  onDeleteQuestion?: (index: number) => void;
  onDragEnd?: (result: any) => void;
  onSave?: () => void;
  isSaving?: boolean;
  hasChanges?: boolean;
  onShowContactDialog: () => void;
  mobileStatsTrigger?: React.ReactNode;
}

const SurveyQuestionsPanel: React.FC<SurveyQuestionsPanelProps> = ({
  generatedQuestions,
  answers,
  mode,
  currentGeneration,
  user,
  toolId,
  currentLang,
  onQuestionChange,
  onAnswerChange,
  onAddQuestion,
  onDeleteQuestion,
  onDragEnd,
  onSave,
  isSaving,
  hasChanges,
  onShowContactDialog,
  mobileStatsTrigger
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <div className="p-4 md:p-8 h-full overflow-auto border-r-0 md:border-r border-border/50">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6 md:mb-8">
        <div className="flex flex-wrap items-center gap-2">
          <FileText className="w-5 h-5 text-primary" />
          <h2 className="text-lg md:text-xl font-semibold">
            {mode === 'edit' ? t('results.surveyTemplateResults.editTitle') : t('results.surveyTemplateResults.title')}
          </h2>
          <Badge variant="secondary">{generatedQuestions.length} {t('results.surveyTemplateResults.questions')}</Badge>
          {mode === 'view' && currentGeneration && user && currentGeneration.user_id === user.id && (
            <Button 
              onClick={() => navigate(`/${currentLang}/tools/${toolId}/results?generationId=${currentGeneration.id}&mode=edit`)}
              variant="outline"
              size="sm"
              className="hidden md:inline-flex"
            >
              <Edit3 className="w-4 h-4 mr-2" />
              {t('results.surveyTemplateResults.editMode')}
            </Button>
          )}
          {mode === 'edit' && (
            <Button 
              onClick={() => navigate(`/${currentLang}/tools/${toolId}/results?generationId=${currentGeneration?.id}&mode=view`)}
              variant="outline"
              size="sm"
              className="hidden md:inline-flex"
            >
              <Eye className="w-4 h-4 mr-2" />
              {t('results.surveyTemplateResults.viewMode')}
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {mobileStatsTrigger && (
            <div className="md:hidden">
              {mobileStatsTrigger}
            </div>
          )}
          <Button onClick={onShowContactDialog} className="bg-gradient-primary text-white">
            <Sparkles className="w-4 h-4 mr-2" />
            {t('results.tryInPlatform')}
          </Button>
        </div>
      </div>
      
      {/* Use GenerationDisplay component */}
      <GenerationDisplay
        questions={generatedQuestions}
        answers={answers}
        mode={mode === 'edit' ? 'edit' : mode === 'answer' ? 'answer' : 'view'}
        onQuestionChange={mode === 'edit' ? onQuestionChange : undefined}
        onAnswerChange={mode === 'answer' ? onAnswerChange : undefined}
        onAddQuestion={mode === 'edit' ? onAddQuestion : undefined}
        onDeleteQuestion={mode === 'edit' ? onDeleteQuestion : undefined}
        onDragEnd={mode === 'edit' ? onDragEnd : undefined}
        onSave={mode === 'edit' ? onSave : undefined}
        isSaving={isSaving}
        hasChanges={hasChanges}
      />
    </div>
  );
};

export default SurveyQuestionsPanel; 